import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, useState } from 'react';
import { TextButton } from '../Button';
import { Spacer } from '../Spacer';
import {
  NoticeContainer,
  NoticeIconWrapper,
  NoticeMessageWrapper,
  NoticeTextContainer,
  NoticeTextWrapper,
  NoticeWrapper,
  MobileButtonsWrapper,
  DesktopButtonsWrapper,
} from './Notice.styles';

export type NoticeVariant = 'warn' | 'alert' | 'success' | 'noIcon' | 'info';
export type NoticeType = 'outlined' | 'borderless' | 'darkmode';

interface NoticeProps {
  variant: NoticeVariant;
  message: string;
  testId: string;
  isMobile?: boolean;
  type?: NoticeType;
  padding?: string;
  onDismiss?: () => void;
}

const MobileButtons: FC<{
  onDismiss: (() => void) | undefined;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ onDismiss, expanded, setExpanded }) => {
  return (
    <MobileButtonsWrapper>
      {onDismiss && <TextButton onClick={onDismiss}>Dismiss</TextButton>}
      <TextButton onClick={() => setExpanded(!expanded)}>
        {expanded ? (
          <>
            View less
            <Spacer />
            <FontAwesomeIcon icon="angle-double-up" />
          </>
        ) : (
          <>
            View More
            <Spacer />
            <FontAwesomeIcon icon="angle-double-down" />
          </>
        )}
      </TextButton>
    </MobileButtonsWrapper>
  );
};

const DesktopButtons: FC<{ onDismiss: (() => void) | undefined }> = ({
  onDismiss,
}) => {
  return (
    <DesktopButtonsWrapper>
      {onDismiss && (
        <TextButton onClick={onDismiss}>
          <FontAwesomeIcon icon="times" />
        </TextButton>
      )}
    </DesktopButtonsWrapper>
  );
};

export const Notice: FC<NoticeProps> = ({
  variant,
  message,
  testId,
  children,
  isMobile,
  type = 'outlined',
  padding,
  onDismiss,
}) => {
  const renderIcon = () => {
    switch (variant) {
      case 'alert':
        return <FontAwesomeIcon icon={'exclamation-circle'} />;
      case 'success':
        return <FontAwesomeIcon icon={'check-circle'} />;
      case 'noIcon':
        return null;
      default:
        return <FontAwesomeIcon icon={'info-circle'} />;
    }
  };

  const [expanded, setExpanded] = useState(false);

  return (
    <NoticeContainer
      variant={variant}
      data-testid={testId}
      isMobile={isMobile}
      type={type}
      padding={padding}
    >
      <NoticeWrapper>
        <NoticeIconWrapper variant={variant}>{renderIcon()}</NoticeIconWrapper>
        <NoticeMessageWrapper isMobile={isMobile} expanded={expanded}>
          {message}
          {children}
        </NoticeMessageWrapper>
      </NoticeWrapper>
      {isMobile ? (
        <MobileButtons
          setExpanded={setExpanded}
          onDismiss={onDismiss}
          expanded={expanded}
        />
      ) : (
        <DesktopButtons onDismiss={onDismiss} />
      )}
    </NoticeContainer>
  );
};

export const NoticeText: FC<NoticeProps> = ({ variant, message, testId }) => {
  return (
    <NoticeTextContainer variant={variant} data-testid={testId}>
      <NoticeTextWrapper>{message}</NoticeTextWrapper>
    </NoticeTextContainer>
  );
};
